import type { FieldExplainerContent } from './types';
import { TAX_PRESET_LABELS, type FilingStatus, type TaxPresetKey, type TaxRates } from '../../engines/equity-core';

export type EquityExplainerContext = {
  input: {
    currentPrice: number;
    growthPct: number;
    horizon: number;
    filingStatus: FilingStatus;
    taxPreset: TaxPresetKey;
  };
  result: {
    rates: TaxRates;
    holdingsCount: number;
    vestedToday: number;
    netToday: number;
    projectedTotal: number;
    netAtHorizon: number;
    endYear: number;
  };
};

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
});

const usdPrecise = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

export function getEquityFieldExplainers(ctx: EquityExplainerContext): FieldExplainerContent[] {
  const { currentPrice, growthPct, horizon, filingStatus, taxPreset } = ctx.input;
  const { rates, holdingsCount, vestedToday, netToday, projectedTotal, netAtHorizon, endYear } = ctx.result;

  const growthRate = growthPct / 100;
  const projectedPrice = currentPrice * Math.pow(1 + growthRate, horizon);
  const holdingsWord = holdingsCount === 1 ? 'holding' : 'holdings';

  return [
    {
      id: 'currentPrice',
      kind: 'input',
      label: 'Current price / share',
      plain:
        'The one share price every holding in your portfolio is valued against today. There is no per-grant override — every RSU, option, RSA, and SAR on this page reads from this single number.',
      formula: `price(year 0) = ${usdPrecise.format(currentPrice)}   (your input)`,
      feeds: `Today's vested value, today's net-if-sold figure, and year 0 of every chart and table on the page across all ${holdingsCount} ${holdingsWord}.`
    },
    {
      id: 'growthPct',
      kind: 'input',
      label: 'Expected annual growth',
      plain:
        'One compounding rate applied to the share price for every future year of the projection — the same assumption whether the grant is RSUs, options, or SARs.',
      formula: `price(year y) = ${usdPrecise.format(currentPrice)} × (1 + ${growthRate})^y\nprice(year ${horizon}) = ${usdPrecise.format(currentPrice)} × (1 + ${growthRate})^${horizon}\n  = ${usdPrecise.format(projectedPrice)}`,
      feeds: 'Every future year of the value chart, the year-by-year table, and the projected totals at your horizon.',
      assumption:
        'A single deterministic growth rate with no volatility. Down years, share buybacks, dilution, and additional funding rounds are not modeled — the price path is a smooth exponential curve.'
    },
    {
      id: 'horizon',
      kind: 'input',
      label: 'Years to project',
      plain: 'How many years forward the price path and every vesting schedule are drawn out.',
      formula: `years shown = 0…${horizon}   (${horizon + 1} rows in the table)`,
      feeds: 'The length of every chart and table, and which vest events are counted toward the projected totals.',
      assumption:
        'If a holding vests over more years than you project here, the still-unvested portion stays counted as unvested for the whole window shown — it is never assumed to have vested early just because the chart ends.'
    },
    {
      id: 'filingStatus',
      kind: 'input',
      label: 'Filing status',
      plain:
        'Married filing jointly is modeled as a flat 4-percentage-point reduction off the ordinary-income and cash-comp rates from your tax profile. Capital-gains and AMT rates are not adjusted.',
      formula:
        filingStatus === 'married_joint'
          ? `ordinary rate = base − 4pp = ${pct(rates.ord)}\n(LTCG stays ${pct(rates.ltcg)}, unchanged)`
          : `filing single: no adjustment applied\nordinary rate = ${pct(rates.ord)}`,
      feeds: 'The ordinary-income rate used to tax every vest event and option spread.',
      assumption:
        'This is a fixed 4-point approximation, not a real joint-bracket recalculation — actual joint-filer savings depend on total household income and vary by bracket.'
    },
    {
      id: 'taxPreset',
      kind: 'input',
      label: 'Tax profile',
      plain:
        'A bundled rate set approximating one state/city combination. This calculator uses three of its rates — ordinary income, long-term capital gains, and ISO state/city — for every tax figure on the page.',
      formula: `${TAX_PRESET_LABELS[taxPreset]}\nordinary = ${pct(rates.ord)}\nLTCG = ${pct(rates.ltcg)}\nISO state/city = ${pct(rates.isoStateCity)}`,
      feeds: 'Ordinary tax at every vest, capital-gains tax on appreciation, and the state/city layer on ISO exercises.',
      assumption:
        'These are flat top-bracket approximations for one representative filer, not a real tax return: no lower-bracket blending, no income thresholds, and no year-to-year rate changes. The preset also bundles an ISO AMT rate, but this calculator does not model AMT — changing that rate never moves a number on this page.'
    },
    {
      id: 'vestedToday',
      kind: 'result',
      label: 'Vested value today',
      plain: 'The pre-tax value, at the current share price, of only the shares already vested across every holding — none of the unvested portion counts here.',
      formula: `full-value grants (RSU/RSA/PSU):\n  vested shares × ${usdPrecise.format(currentPrice)}\noptions and SARs:\n  vested × max(0, ${usdPrecise.format(currentPrice)} − strike or baseline)\nsummed across ${holdingsCount} ${holdingsWord}\n  = ${usd.format(vestedToday)}`,
      feeds: 'Nothing further downstream; it is the headline "what could I sell right now" figure.'
    },
    {
      id: 'netToday',
      kind: 'result',
      label: 'Net if sold today',
      plain:
        'What vested value today becomes after tax, if every vested share were sold at the current price. Full-value grants (RSU/RSA/PSU) pay capital-gains tax only on the appreciation since vest; options and SAR/phantom pay tax on the spread above strike or baseline, at ordinary or capital-gains rates depending on the instrument.',
      formula: `${usd.format(vestedToday)} (pre-tax)\n− tax on gains above cost basis, at your tax profile's rates\n  = ${usd.format(netToday)}`,
      feeds: 'Nothing further downstream; it is a standalone liquidity check.',
      assumption: 'Ignores brokerage fees, wash-sale rules, and any AMT credit carryforward from prior ISO exercises.'
    },
    {
      id: 'projectedTotal',
      kind: 'result',
      label: `Projected total (yr ${endYear})`,
      plain: `Vested plus still-unvested value at year ${endYear}, valued at that year's projected share price. This is the full stack, whether or not you could actually sell the unvested portion.`,
      formula: `vested(yr ${endYear}) + unvested(yr ${endYear}), both at price ${usdPrecise.format(projectedPrice)}\n  = ${usd.format(projectedTotal)}`,
      feeds: 'The "total value" line on the equity chart and the per-grant breakdown table.',
      assumption: 'Unvested value assumes you stay employed through every remaining vest date — nothing here models a departure or a cliff you leave before reaching.'
    },
    {
      id: 'netAtHorizon',
      kind: 'result',
      label: `Net if liquidated (yr ${endYear})`,
      plain: `After-tax cash from selling only the vested portion at year ${endYear}, at that year's projected price — the unvested portion is excluded because you cannot sell what has not vested.`,
      formula: `vested(yr ${endYear}) after tax on gains and any ordinary income recognized at each vest\n  = ${usd.format(netAtHorizon)}`,
      feeds: 'Nothing further downstream; it is the long-range liquidity check.',
      assumption: `Everything vested is liquidated together at year ${endYear}, at that year's single projected price — not sold off vest by vest. Ordinary income was still recognized back in each vest year; what is taxed at the sale is the appreciation from each tranche's vest-date basis up to the year-${endYear} price.`
    }
  ];
}
